import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import agentApiService from '../../services/agentApiService';
import { toast } from 'react-toastify';

const BookingCancellations = () => {
  const navigate = useNavigate();
  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCancellations, setFilteredCancellations] = useState([]);

  useEffect(() => {
    console.log('🔍 [BookingCancellations] Component mounted, fetching data...');
    fetchCancellations();
    fetchStats();
  }, [currentPage, pageSize]);

  useEffect(() => {
    // Filter cancellations based on search term
    if (searchTerm.trim() === '') {
      setFilteredCancellations(cancellations);
    } else {
      const filtered = cancellations.filter(cancellation =>
        cancellation.cancellationId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cancellation.bookingId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cancellation.hotelName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cancellation.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cancellation.passengerFirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cancellation.passengerLastName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCancellations(filtered);
    }
  }, [cancellations, searchTerm]);

  const fetchCancellations = async () => {
    try {
      setLoading(true);
      console.log('🔍 [BookingCancellations] Fetching cancellations from API...');
      
      const response = await agentApiService.getBookingCancellations(currentPage, pageSize);
      console.log('📊 [BookingCancellations] API Response:', response);
      
      if (response.success) {
        setCancellations(response.content || []);
        setTotalPages(response.totalPages || 0);
        setTotalElements(response.totalElements || 0);
        setHasNext(response.hasNext || false);
        setHasPrevious(response.hasPrevious || false);
        
        console.log('✅ [BookingCancellations] Data loaded successfully:', {
          totalElements: response.totalElements,
          totalPages: response.totalPages,
          contentLength: response.content?.length || 0
        });
      } else {
        console.error('❌ [BookingCancellations] API returned error:', response.message);
        setError(response.message || 'Failed to fetch cancellations');
        toast.error(response.message || 'Failed to fetch cancellations');
      }
    } catch (error) {
      console.error('❌ [BookingCancellations] Network/API error:', error);
      setError(error.message);
      toast.error('Failed to fetch cancellations: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      console.log('📊 [BookingCancellations] Fetching stats from API...');
      const response = await agentApiService.getCancellationStats();
      console.log('📊 [BookingCancellations] Stats Response:', response);
      
      if (response.success) {
        setStats(response.stats);
        console.log('✅ [BookingCancellations] Stats loaded successfully:', response.stats);
      } else {
        console.warn('⚠️ [BookingCancellations] Stats API returned error:', response.message);
      }
    } catch (error) {
      console.error('❌ [BookingCancellations] Stats API error:', error);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(parseInt(newSize));
    setCurrentPage(0); // Reset to first page
  };

  const handleCancellationClick = (cancellationId) => {
    navigate(`/agent/dashboard/booking-cancellation/${cancellationId}`);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'CANCELLED':
      case 'AUTO_CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Error Loading Cancellations</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Booking Cancellations</h1>
        <p className="text-gray-600">Manage and view all booking cancellations</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="text-2xl font-bold text-blue-600">{stats.totalCancellations}</div>
            <div className="text-sm text-gray-600">Total Cancellations</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="text-2xl font-bold text-red-600">{stats.cancelledCount}</div>
            <div className="text-sm text-gray-600">Cancelled</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="text-2xl font-bold text-yellow-600">{stats.inProgressCount}</div>
            <div className="text-sm text-gray-600">In Progress</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="text-2xl font-bold text-green-600">{stats.completionRate?.toFixed(1)}%</div>
            <div className="text-sm text-gray-600">Completion Rate</div>
          </div>
        </div>
      )}

      {/* Search and Controls */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search by cancellation ID, booking ID, hotel name, city, or passenger name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Show:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 text-sm"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Cancellations Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cancellation ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hotel Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Check-In
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Check-Out
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  City
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Booking Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cancellation Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCancellations.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <div className="text-4xl mb-4">📋</div>
                      <div className="text-lg font-medium mb-2">
                        {totalElements === 0 ? 'No cancellation records in database' : 'No cancellations found'}
                      </div>
                      <div className="text-sm">
                        {searchTerm ? (
                          'Try adjusting your search criteria'
                        ) : totalElements === 0 ? (
                          <div className="space-y-2">
                            <div>The booking cancellation system is working correctly.</div>
                            <div>Cancellation records will appear here when bookings are cancelled.</div>
                            <div className="text-xs text-gray-400 mt-4">
                              API Status: ✅ Connected | Database: ✅ Accessible | Records: {totalElements}
                            </div>
                          </div>
                        ) : (
                          'No cancellation records available'
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredCancellations.map((cancellation) => (
                  <tr
                    key={cancellation.cancellationId}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleCancellationClick(cancellation.cancellationId)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-blue-600 hover:text-blue-800">
                        {cancellation.cancellationId}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {cancellation.hotelName || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(cancellation.checkInDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(cancellation.checkOutDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {cancellation.city || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {cancellation.passengerPhoneNo || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(cancellation.bookingDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(cancellation.cancellationDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(cancellation.status)}`}>
                        {cancellation.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!hasPrevious}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!hasNext}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">{currentPage * pageSize + 1}</span>
                    {' '}to{' '}
                    <span className="font-medium">
                      {Math.min((currentPage + 1) * pageSize, totalElements)}
                    </span>
                    {' '}of{' '}
                    <span className="font-medium">{totalElements}</span>
                    {' '}results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!hasPrevious}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = Math.max(0, Math.min(currentPage - 2 + i, totalPages - 1));
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === currentPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum + 1}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!hasNext}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingCancellations;